/**
 * PinnedMessagesPanel - Pinned messages list for current channel.
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useChat } from '../../contexts/ChatContext';
import { chatPinsApi } from '../../services/chatApi';
import type { ChatMessage } from '../../types/chat';

interface PinnedMessagesPanelProps {
  onClose: () => void;
}

const PinnedMessagesPanel: React.FC<PinnedMessagesPanelProps> = ({ onClose }) => {
  const { t } = useTranslation('chat');
  const { currentChannel } = useChat();
  const [pinned, setPinned] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!currentChannel) return;
    setLoading(true);
    chatPinsApi.list(currentChannel.id).then(setPinned).catch(console.error).finally(() => setLoading(false));
  }, [currentChannel]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h3 className="font-semibold text-sm">{t('pinnedMessages')}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
          </div>
        ) : pinned.length > 0 ? (
          pinned.map(msg => (
            <div key={msg.id} className="px-4 py-3 border-b hover:bg-gray-50">
              <div className="text-xs text-gray-500 mb-1">
                User {msg.user_id} - {new Date(msg.created_at).toLocaleDateString()}
              </div>
              <div className="text-sm text-gray-800">{msg.content}</div>
            </div>
          ))
        ) : (
          <div className="text-center text-gray-400 text-sm py-8">{t('noPinnedMessages')}</div>
        )}
      </div>
    </div>
  );
};

export default PinnedMessagesPanel;
